'use client';
import React from 'react';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { FilterChipsGroup } from './FilterChipsGroup';
import { FilterGroup } from './FilterGroup';
import { TRANSLATIONS } from './i18n';
import { useProductFilterState } from './ProductFilterProvider';
import { optionKeyOf } from './productFilterTypes';

/** One group's identity, as the analytics plan reports a group rather than an option. */
export type FilterGroupRef = {
  /** Absent on a chips group: Strapi hides the field in chips mode. */
  categoryId?: string;
  categoryName?: string;
};

export type FilterToggle = FilterGroupRef & {
  /** Always present here — a checkbox group is unselectable without one. */
  categoryId: string;
  value: string;
  label: string;
  /** The state the click produced — the event names the result, not the intent. */
  checked: boolean;
};

export type FilterChipActivation = FilterGroupRef & {
  label: string;
  url: string;
};

export type FilterGroupsProps = {
  /**
   * Per-option product counts, keyed by `categoryId`. Optional: the panel
   * renders without them rather than showing a zero it cannot substantiate.
   */
  counts?: Readonly<Record<string, ReadonlyMap<string, number>>>;
  /** A filter was ticked or unticked. Receives the group and option, for analytics. */
  onFilterChange?: (toggle: FilterToggle) => void;
  /** A navigation chip was activated. Reported separately (CMS-1915 #2530). */
  onChipActivate?: (activation: FilterChipActivation) => void;
};

/**
 * Every resolved group, in the order the editor arranged them. Reads the groups
 * from the provider rather than taking them as a prop, so the control and the
 * results cannot be given two different lists.
 */
export const FilterGroups: React.FC<FilterGroupsProps> = ({
  counts,
  onFilterChange,
  onChipActivate,
}) => {
  const { brand, lang } = useCmsClientContext();
  const t = getClientTranslations(TRANSLATIONS, { brand, lang });
  const { categories, selection, availability, toggleValue } =
    useProductFilterState();

  return (
    <>
      {categories.map((category) => {
        // Navigation, not a filter: no checkboxes, no selection, no Clear All
        // (CMS-1893 R3.2 / CMS-1542 R4.5). Keyed by name and first destination
        // because a chips group has no `categoryId` — the field is hidden in
        // chips mode, so Strapi strips it on save.
        if (category.displayMode === 'CHIPS') {
          return (
            <FilterChipsGroup
              key={`chips:${category.name ?? ''}:${category.chips[0]?.url ?? ''}`}
              category={category}
              onActivate={(chip) =>
                onChipActivate?.({
                  // Absent here by design, per the note above; the group's name
                  // is what identifies it.
                  categoryId: category.categoryId,
                  categoryName: category.name,
                  ...chip,
                })
              }
            />
          );
        }

        const categoryId = category.categoryId!;

        return (
          <FilterGroup
            key={categoryId}
            category={category}
            selected={selection[categoryId] ?? []}
            counts={counts?.[categoryId]}
            available={availability?.[categoryId]}
            // A group's own labels win; the dictionary is the fallback for a
            // group that left them empty.
            showAllLabel={category.collapsedLabel ?? t.sort_and_filter_show_all}
            showLessLabel={
              category.expandedLabel ?? t.sort_and_filter_show_less
            }
            onToggle={(key) => {
              toggleValue(categoryId, key);
              onFilterChange?.({
                categoryId,
                categoryName: category.name,
                value: key,
                // Matched on the key the toggle handed back, not on `value`:
                // a band's options all share one `value`, so that lookup found
                // nothing and reported the key in place of the reader's label.
                label:
                  category.options.find((option) => optionKeyOf(option) === key)
                    ?.label ?? key,
                // Read off the pre-toggle selection rather than a re-render:
                // the handler runs before the provider's state has settled.
                checked: !(selection[categoryId] ?? []).includes(key),
              });
            }}
          />
        );
      })}
    </>
  );
};
